import { ContextProvider } from "@lit/context";
import { css, html, PropertyValues, TemplateResult } from "lit";
import { customElement } from "lit/decorators.js";
import {
  Color,
  DirectionalLight,
  Object3D,
  Object3DEventMap,
  Texture,
  Vector3,
} from "three";
import { EngineCamera } from "../../engine/camera/Camera";
import { Engine, Entity } from "../../engine/Engine";
import { BaseElement } from "../ui-kit/BaseElement";
import { waitTill } from "../../util/Wait";
import { logger } from "../../util/Logger";
import {
  DEFAULT_ENVIRONMENT_URL,
  loadEnvironmentTexture,
} from "../pipeline/loadEnvironmentTexture";
import { ElementEventPlatform } from "../events/ElementEventPlatform";
import { ViewportContext, viewportContext } from "./ViewportContext";

const WEBGL_RENDER_SCENE_CHANGED_EVENT = "webgl-render-scene-changed";

export class SugarObject extends BaseElement {
  protected renderHtml(): TemplateResult {
    return html``;
  }
  protected override useShadowRoot = false;

  connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener("object-created", this.objectCreatedHandler);
  }

  disconnectedCallback(): void {
    this.removeEventListener("object-created", this.objectCreatedHandler);
    super.disconnectedCallback();
  }

  static properties = {
    position: { type: Array },
    rotation: { type: Array },
    scale: { type: Array },
    color: { type: String },
  };
  position = [0, 0, 0];
  rotation = [0, 0, 0];
  scale = [1, 1, 1];
  color = "#ffffff";
  object?: Object3D;

  constructor() {
    super();
    this.position = [0, 0, 0];
    this.rotation = [0, 0, 0];
    this.scale = [1, 1, 1];
    this.color = "#ffffff";
  }

  createRenderRoot() {
    return this;
  }

  createObject3D() {
    this.object = new Object3D();
    return this.object;
  }

  private readonly objectCreatedHandler = async (event: Event) => {
    if (!(event instanceof CustomEvent)) return;
    const object = event.detail as Object3D;
    if (this.object === object) return;
    await waitTill(() => this.object != null && this.object != undefined);
    if (this.isConnected) this.object?.add(object);
  };

  firstUpdated(e: any) {
    super.firstUpdated(e);

    this.object = this.createObject3D();
    this.updateTransform();
    this.parentElement?.dispatchEvent(
      new CustomEvent("object-created", {
        detail: this.object,
        bubbles: true,
      }),
    );
  }

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    if (this.object) {
      if (
        changedProperties.has("position") ||
        changedProperties.has("rotation") ||
        changedProperties.has("scale")
      ) {
        this.updateTransform();
      }
      if (changedProperties.has("color")) {
        //this.updateColor();
      }
    }
  }

  updateTransform() {
    if (!this.object) return;
    this.object.position.set(
      this.position[0],
      this.position[1],
      this.position[2],
    );
    this.object.rotation.set(
      this.rotation[0],
      this.rotation[1],
      this.rotation[2],
    );
    this.object.scale.set(this.scale[0], this.scale[1], this.scale[2]);
  }
}

@customElement("sugar-light")
export class SugarLight extends SugarObject {
  static properties = {
    position: { type: Array },
    rotation: { type: Array },
    scale: { type: Array },
    color: { type: String },
    intensity: { type: Number },
  };
  protected update(changedProperties: PropertyValues): void {
    super.update(changedProperties);
    if (changedProperties.has("color") || changedProperties.has("intensity")) {
      /*const l = this.object as DirectionalLight;
      l.color = new Color(this.color);
      l.intensity = this.intensity;*/
    }
  }

  intensity = 1;

  createObject3D(): Object3D<Object3DEventMap> {
    logger.info("intensity", this.intensity);
    const l = new DirectionalLight(new Color(this.color), this.intensity);
    l.castShadow = true;
    return l;
  }
}

export class SugarElement extends SugarObject {
  protected renderHtml(): TemplateResult {
    return html``;
  }
  protected override useShadowRoot = false;

  static properties = {
    position: { type: Array },
    rotation: { type: Array },
    scale: { type: Array },
    color: { type: String },
  };
  position = [0, 0, 0];
  rotation = [0, 0, 0];
  scale = [1, 1, 1];
  color = "#ffffff";
  entity?: Entity;

  get eventPlatform(): ElementEventPlatform | undefined {
    return (
      this.closest("sugar-viewport, sugar-canvas") as SugarViewport | null
    )?.eventPlatform;
  }

  constructor() {
    super();
    this.position = [0, 0, 0];
    this.rotation = [0, 0, 0];
    this.scale = [1, 1, 1];
    this.color = "#ffffff";
  }

  createRenderRoot() {
    return this;
  }

  createEntity(): Entity | undefined {
    return undefined;
  }

  firstUpdated(e: any) {
    this.entity = this.createEntity();
    this.dispatchEvent(
      new CustomEvent("entity-created", {
        detail: this.entity,
        bubbles: true,
        composed: true,
      }),
    );
  }
}

@customElement("sugar-viewport")
export class SugarViewport extends SugarObject {
  static override styles = css/*css*/ `
    sugar-viewport,
    sugar-canvas {
      display: block;
      width: 100%;
      height: 100%;
      box-sizing: border-box;
      background: radial-gradient(
        ellipse 52% 48% at 50% 42%,
        var(--srd-bg) 0%,
        var(--srd-bg) 22%,
        color-mix(in srgb, var(--srd-bg) 78%, var(--srd-bg-muted)) 52%,
        color-mix(in srgb, var(--srd-bg) 42%, var(--srd-bg-muted)) 78%,
        var(--srd-bg-muted) 100%
      );
    }
  `;

  static properties = {
    ...SugarObject.properties,
    rendererBackend: { type: String, attribute: "renderer-backend" },
  };

  /**
   * `webgl` = classic WebGLRenderer (required by UV radiance / Fake Lumen).
   * Default `webgpu` matches Engine.
   */
  rendererBackend: "webgl" | "webgpu" = "webgpu";

  protected override useShadowRoot = false;

  readonly eventPlatform = new ElementEventPlatform();
  private readonly viewportContextProvider = new ContextProvider(this, {
    context: viewportContext,
    initialValue: undefined,
  });
  private activeViewportContext?: ViewportContext;

  private _engine?: Engine;
  get engine(): Engine | undefined {
    return this._engine;
  }

  set engine(value: Engine | undefined) {
    this._engine = value;
  }

  protected get viewportObjectName(): string {
    return "SugarViewport";
  }

  private initializationTimer?: ReturnType<typeof setTimeout>;
  private initialized = false;

  firstUpdated(e: any): void {
    super.firstUpdated(e);
    this.object!.name = this.viewportObjectName;
    this.scheduleInitialization();
  }

  private readonly entityCreatedHandler = async (event: Event) => {
    if (!(event instanceof CustomEvent)) return;
    const entity = event.detail as Entity;
    await waitTill(() => this.engine != null && this.engine != undefined);
    if (this.isConnected && entity && this.engine) {
      this.engine.addEntity(entity);
    }
  };

  connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener("entity-created", this.entityCreatedHandler);
    if (this.initialized) {
      this.reattach();
    } else if (this.object) {
      this.scheduleInitialization();
    }
  }

  disconnectedCallback(): void {
    if (this.initializationTimer !== undefined) {
      clearTimeout(this.initializationTimer);
      this.initializationTimer = undefined;
    }
    this.removeEventListener("entity-created", this.entityCreatedHandler);
    const context = this.activeViewportContext;
    this.activeViewportContext = undefined;
    try {
      context?.dispose();
    } catch (error) {
      logger.error("Viewport context disposal failed", error);
    } finally {
      this.viewportContextProvider.value = undefined;
    }
    this.eventPlatform.unbindLegacyEngine();
    if (this._engine?.bindedElement === this) {
      this._engine.bindElement(undefined);
    }
    super.disconnectedCallback();
  }

  private scheduleInitialization(): void {
    if (
      this.initialized ||
      this.initializationTimer !== undefined ||
      !this.isConnected
    ) {
      return;
    }
    this.initializationTimer = setTimeout(() => {
      this.initializationTimer = undefined;
      void this.initializeViewport();
    }, 10);
  }

  private async initializeViewport(): Promise<void> {
    if (this.initialized || !this.isConnected) return;

    this.setAttribute("mobiledragover", "");
    this.setAttribute("mobiledrop", "");
    this.classList.add("center");
    this.classList.add("dropArea");

    const engine =
      this._engine ??
      new Engine(undefined, {
        rendererBackend:
          this.rendererBackend === "webgl" ? "webgl" : "webgpu",
      });
    await engine.ensureReady();
    if (!this.isConnected) return;

    engine.bindElement(this);
    if (!this.hasAttribute("no-scene-tuning")) {
      engine.setUseRenderSceneSwap(false);
      engine.setRenderSceneMergeMeshesPerMaterial(false);
      engine.setDebugLogRenderScene(false);
    }
    this._engine = engine;
    this.eventPlatform.bindLegacyEngine(engine);
    this._engine.activeScene?.add(this.object!);

    if (!this.hasAttribute("no-environment")) {
      this.loadEnvironment(DEFAULT_ENVIRONMENT_URL, {
        rotationY: Math.PI * 0.2,
      });
    }

    if (!this.hasAttribute("no-default-camera")) {
      const activeCamera = new EngineCamera();
      activeCamera.instance.position.set(-1.0, 0.5, 1.0);
      activeCamera.instance.lookAt(new Vector3(0, 0, 0));
      this._engine.activeCamera = activeCamera;
    }

    this.initialized = true;
    this.attachViewportContext();
    this._engine.start();
    this.scheduleResize();
    this.dispatchEngineReady();
  }

  private reattach(): void {
    if (!this._engine || !this.isConnected) return;
    this._engine.bindElement(this);
    this.eventPlatform.bindLegacyEngine(this._engine);
    this.attachViewportContext();
    this.scheduleResize();
    this.dispatchEngineReady();
  }

  private attachViewportContext(): void {
    if (!this._engine || this.activeViewportContext) return;
    const context = new ViewportContext(this, this._engine);
    this.activeViewportContext = context;
    this.viewportContextProvider.value = context;
    context.announceReady();
  }

  private scheduleResize(): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.isConnected) this._engine?.resize(true);
      });
    });
  }

  private dispatchEngineReady(): void {
    this.dispatchEvent(
      new CustomEvent("engine-ready", {
        detail: this._engine,
        bubbles: true,
        composed: true,
      }),
    );
  }

  private _environmentTexture?: Texture;

  private _environmentRotationY = 0;
  async loadEnvironment(
    url: string,
    options: { rotationY?: number } = {},
  ): Promise<Texture | undefined> {
    if (!url) return undefined;

    try {
      const texture = await loadEnvironmentTexture(url);
      if (!texture) return undefined;

      await waitTill(() => this.engine?.activeScene != null);
      this.engine!.activeScene!.environment = texture;
      this._environmentTexture = texture;
      if (options.rotationY != null) {
        this.setEnvironmentRotationY(options.rotationY);
      }
      this.engine?.dispatchCustomEvent(WEBGL_RENDER_SCENE_CHANGED_EVENT);

      return texture;
    } catch (err) {
      logger.error("loadEnvironment: failed to load", url, err);
      return undefined;
    }
  }

  setEnvironmentRotationY(radians: number): void {
    const scene = this.engine?.activeScene;
    if (!scene) return;
    scene.environmentRotation.x = radians;
    scene.backgroundRotation.x = radians;
  }

  protected renderHtml() {
    return html``;
  }
}

/**
 * @deprecated Use `<sugar-viewport>` for new integrations.
 */
@customElement("sugar-canvas")
export class SugarCanvas extends SugarViewport {
  protected override get viewportObjectName(): string {
    return "SugarCanvas";
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "sugar-viewport": SugarViewport;
    "sugar-canvas": SugarCanvas;
  }
}
